import { Box, Link, Typography, DialogActions, Button, Alert, CircularProgress } from '@mui/material';
import ResponsiveDialogue from '../../ResponsiveDialogue/ResponsiveDialogue.tsx';
import { type ChangeEvent, useState } from 'react';
import { showAlert } from '../../../redux/slices/alertSlice.ts';
import {
	UPLOAD_BULK_UDF_DEFAULT_ERROR,
	UPLOAD_BULK_UDF_HEADER_MESSAGE,
	UPLOAD_BULK_UDF_NO_FILE_ERROR,
	UPLOAD_BULK_UDF_SUCCESS,
	UPLOAD_BULK_UDF_TITLE,
} from '../../../constants/messages.tsx';
import { useUploadLeasedAssetUDFChangesMutation } from '../../../api/asset.api.ts';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccount } from '../../../redux/slices/accountSlice.ts';
import {
	acknowledged,
	selectLeasedUdfInProgress,
	setUploadInProgress,
} from '../../../redux/slices/udfUpdateSlice.ts';

interface UploadLeasedAssetUDFModalProps {
	open: boolean;
	onClose: () => void;
	onUploadSuccess?: () => void;
}

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

export default function UploadLeasedAssetUDFModal({ open, onClose }: UploadLeasedAssetUDFModalProps) {
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const account = useSelector(selectAccount);
	const uploadInProgress = useSelector(selectLeasedUdfInProgress);
	const dispatch = useDispatch();
	const [uploadLeasedAssetUDFChanges, { isLoading }] = useUploadLeasedAssetUDFChangesMutation();

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		setError(null);
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		if (!selectedFile.name.endsWith('.csv')) {
			setError('File must be a .csv file.');
			setFile(null);
			return;
		}
		if (selectedFile.size > MAX_FILE_SIZE) {
			setError(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
			setFile(null);
			return;
		}
		// Add more file name checks if needed
		setFile(selectedFile);
	};

	const handleUpload = async () => {
		setError(null);
		if (!file) {
			setError(UPLOAD_BULK_UDF_NO_FILE_ERROR);
			return;
		}
		try {
			dispatch(setUploadInProgress());
			const result = await uploadLeasedAssetUDFChanges({
				file: file,
				accountId: account.accountId
			}).unwrap();
			if (!result.success) {
				const allErrors = result.errors
					.map((e) => `Row ${e.row}, Column "${e.column}": ${e.message}`)
					.join('; \n\n');
				throw new Error(allErrors || UPLOAD_BULK_UDF_DEFAULT_ERROR);
			}
			dispatch(
				showAlert({
					message: UPLOAD_BULK_UDF_SUCCESS,
					type: 'success',
				})
			);
			setFile(null);
			setError(null);
			onClose();
		} catch (err: any) {
			dispatch(acknowledged());
			setError(err.message || UPLOAD_BULK_UDF_DEFAULT_ERROR);
		}
	};

	if (!open) {
		return null;
	}

	return (
		<ResponsiveDialogue
		data-testid="upload-leased-asset-dialogue"
		open={open}
		onClose={onClose}
		title={UPLOAD_BULK_UDF_TITLE}
		width="450px"
	>
		<Box sx={{ display: 'flex', justifyContent: 'flex-start', padding: 2 }}>
			<Box sx={{ mb: 1 }}>
				<Typography color="text.secondary" sx={{ mb: 2, display: 'block' }}>{UPLOAD_BULK_UDF_HEADER_MESSAGE}</Typography>
				<input
					id="upload-leased-assets-file-input"
					type="file"
					accept=".csv"
					style={{ display: 'none' }}
					onChange={handleFileChange}
				/>
				<label htmlFor="upload-leased-assets-file-input">
					<Link
						component="span"
						variant="button"
						sx={{
							cursor: 'pointer',
							textTransform: 'none',
							p: 0,
							minWidth: 0,
							color: (theme) => theme.palette.brand.primary,
						}}
						underline="hover"
					>
						Select File &gt;
					</Link>
					{file && (
						<Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
							{file.name}
						</Typography>
					)}
				</label>
				<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
					Note: Files must be in .csv format and limited to {MAX_FILE_SIZE_MB}MB.
				</Typography>
				{error && (
					<Alert severity="error" sx={{ mb: 1 }}>
						{error}
					</Alert>
				)}
			</Box>
		</Box>
		<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
			<DialogActions>
				<Button onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleUpload}
					disabled={isLoading || uploadInProgress}
					sx={{ width: '80px' }}
				>
					{isLoading ? <CircularProgress size={24} color="inherit" /> : 'Import'}
				</Button>
			</DialogActions>
		</Box>
	</ResponsiveDialogue>);
}

